import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import {
  getModeLabel,
  getModeTemplate,
  type DataSettings,
  type DistrictMode,
  type FieldDef,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import { DataSettingsFieldList } from './DataSettingsFieldList';
import {
  countRequiredUserFields,
  deriveUserProfileOptions,
  isFieldKeyFixed,
  makeNewCustomField,
  mergeFields,
  renameFieldInList,
  serializeField,
  type KeyRenameResult,
} from './dataSettingsHelpers';

/**
 * 地区別「データ設定」ページ (Sprint BG-DataSettings 3、A-S9a / GAP-A15 MUST close)。
 *
 * 旧 v1 `old/apps/admin/src/pages/DataSettingsPage.tsx` (511 行) を踏襲。ユーザー指摘の
 * 「『地区詳細』『データ設定』内に『ユーザー設定の入力項目』『投稿時の入力項目』設定 UI
 * が存在しない」への正式対応。
 *
 * 機能:
 *   - 地区の保存値を読込む。保存値が空ならモードの組込テンプレートを初期値にする (mergeFields)
 *   - userFields / postFields の 2 section editor (DataSettingsFieldList)
 *   - 10 型対応 (text/textarea/select/multiselect/number/checkbox/date/location/photo/rating-scale)
 *   - 3 mode (required/optional/hidden) bar
 *   - 項目の追加・削除・内部キー変更 (制限はシステムが内部キーで扱う項目だけ、dataSettingsHelpers の isFieldDeletable / isFieldKeyFixed)
 *   - displayOrder / visible / useAsAnalysisAxis / definition 編集
 *   - rating-scale 固有 optionsMeta (min/max/excludeZero/scaleLabels)
 *   - 保存ガード: userFields に required >= 1 必須
 *   - userProfileOptions (relationships / attributes) を userFields から逆算して同期保存
 *
 * 認可: master role + 当該地区の地区管理者 (role='district' かつ districts に含む) が書込可。
 * いずれも書込むのは地区 doc の `dataSettings` override (テンプレ本体ではない)。rules 側も
 * field-scoped で dataSettings / userProfileOptions を地区管理者へ開放済 (firestore.rules districts update)。
 */

export function DataSettingsPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const districtState = useDistrict(districtId ?? null);
  const { state: authState } = useAdminAuth();
  const { toast } = useToast();
  const [settings, setSettings] = useState<DataSettings | null>(null);
  const [saving, setSaving] = useState(false);

  const isMaster = authState.role === 'master';
  const uid = authState.user?.uid ?? '';
  // master は全地区、地区管理者 (role='district') は担当地区 (districts claim) のみ編集可。
  // 書込対象は地区 doc の dataSettings override で、firestore.rules も field-scoped で許可済。
  const canEdit =
    isMaster ||
    (authState.role === 'district' && !!districtId && authState.districts.includes(districtId));

  const district = districtState.status === 'ready' ? districtState.district : null;
  const mode: DistrictMode = (district?.mode as DistrictMode | undefined) ?? 'bousai';

  // 地区 load 完了時に template default + 保存値を merge して state に展開
  useEffect(() => {
    if (!district) return;
    const tpl = getModeTemplate(mode);
    const saved = (district.dataSettings ?? {}) as Partial<DataSettings>;
    setSettings({
      userFields: mergeFields(tpl.dataSettings.userFields, saved.userFields ?? [], 'userFields'),
      postFields: mergeFields(tpl.dataSettings.postFields, saved.postFields ?? [], 'postFields'),
    });
    // mode 変更時にも再 merge する (district.mode が編集された場合)
  }, [district, mode]);

  const userRequiredCount = useMemo(
    () => (settings ? countRequiredUserFields(settings.userFields) : 0),
    [settings],
  );

  function updateField(
    section: 'userFields' | 'postFields',
    key: string,
    partial: Partial<FieldDef>,
  ) {
    setSettings((prev) =>
      prev
        ? {
            ...prev,
            [section]: prev[section].map((f) => (f.key === key ? { ...f, ...partial } : f)),
          }
        : prev,
    );
  }

  /**
   * 内部キーのリネーム (A-④ 退行修正、old2 `renameField` 踏襲)。
   * 空キー / 重複キーは検証で拒否し、成否を boolean+error で返す。成功時のみ
   * 親 state を更新する。FieldList 側はこの結果を見て draft を巻き戻し inline 表示する。
   */
  function renameField(
    section: 'userFields' | 'postFields',
    oldKey: string,
    newKey: string,
  ): KeyRenameResult {
    if (!settings) return { ok: false, error: '読み込み中です' };
    // システムが扱う内部キー (nickname / gender 等) への変更は、保存先や画面の扱いが変わるので止める。
    if (newKey !== oldKey && isFieldKeyFixed(section, newKey)) {
      return { ok: false, error: `「${newKey}」はシステムが使う内部キーのため使えません` };
    }
    const { fields, result } = renameFieldInList(settings[section], oldKey, newKey);
    if (result.ok) {
      setSettings((prev) => (prev ? { ...prev, [section]: fields } : prev));
    }
    return result;
  }

  function addField(section: 'userFields' | 'postFields') {
    setSettings((prev) =>
      prev ? { ...prev, [section]: [...prev[section], makeNewCustomField()] } : prev,
    );
  }

  function removeField(section: 'userFields' | 'postFields', key: string) {
    if (!confirm('この項目を削除しますか？')) return;
    setSettings((prev) =>
      prev ? { ...prev, [section]: prev[section].filter((f) => f.key !== key) } : prev,
    );
  }

  async function handleSave() {
    if (!district || !settings || !canEdit) return;

    // 旧 v1 と同じガード: userFields に required >= 1
    if (countRequiredUserFields(settings.userFields) === 0) {
      toast(
        'error',
        '⚠ 保存できません: ユーザー設定に必須項目が 1 件もありません。1 つ以上を必須に設定してください。',
      );
      return;
    }

    setSaving(true);
    try {
      const ref = doc(db, 'districts', district.id);
      const userProfileOptions = deriveUserProfileOptions(settings.userFields);
      await setDoc(
        ref,
        {
          dataSettings: {
            userFields: settings.userFields.map(serializeField),
            postFields: settings.postFields.map(serializeField),
          },
          userProfileOptions,
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        },
        { merge: true },
      );
      toast(
        'success',
        `保存しました (userFields: ${settings.userFields.length} 件 / postFields: ${settings.postFields.length} 件)`,
      );
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  if (districtState.status === 'loading') {
    return <Card padding="md">読み込み中…</Card>;
  }
  if (districtState.status === 'not-found') {
    return (
      <Card padding="md">
        <p className="text-sm text-slate-700">地区が見つかりません。</p>
        <Link to="/districts" className="text-sm text-primary-600 hover:underline">
          地区一覧へ
        </Link>
      </Card>
    );
  }
  if (districtState.status === 'error') {
    return (
      <Card padding="md">
        <p className="text-sm text-danger-700">エラー: {districtState.error.message}</p>
      </Card>
    );
  }
  if (!settings) {
    return <Card padding="md">読み込み中…</Card>;
  }

  return (
    <div className="grid grid-cols-1 gap-4">
      <div>
        <Link
          to={`/districts/${district?.id ?? ''}`}
          className="text-sm text-primary-600 hover:underline"
        >
          ← 地区詳細へ戻る
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">入力項目（ユーザー・投稿）</h1>
        <p className="text-xs text-slate-500 mt-1">
          地区: <span className="font-medium text-slate-800">{district?.name ?? districtId}</span> /
          モード:{' '}
          <span className="font-medium text-slate-800">
            {getModeLabel(mode, district?.modeLabel)}
          </span>
          （地区詳細ページで変更可能）
        </p>
      </div>

      {!canEdit && (
        <Card padding="md" className="border-warning-300 bg-warning-50">
          <p className="text-sm text-warning-800">
            閲覧のみ。この地区の入力項目を編集する権限がありません。
          </p>
        </Card>
      )}

      {userRequiredCount === 0 && (
        <Card padding="md" className="border-danger-300 bg-danger-50">
          <p className="text-sm text-danger-800">
            ⛔ ユーザー設定に「必須」項目が 1 件もありません。少なくとも 1 つは「必須」項目を
            設定するまで保存できません。
          </p>
        </Card>
      )}

      <DataSettingsFieldList
        title="ユーザー設定の入力項目"
        source="userFields"
        fields={settings.userFields}
        disabled={!canEdit || saving}
        onChange={(key, p) => updateField('userFields', key, p)}
        onRenameKey={(oldKey, newKey) => renameField('userFields', oldKey, newKey)}
        onAdd={() => addField('userFields')}
        onRemove={(key) => removeField('userFields', key)}
      />

      <DataSettingsFieldList
        title="投稿時の入力項目"
        source="postFields"
        fields={settings.postFields}
        disabled={!canEdit || saving}
        onChange={(key, p) => updateField('postFields', key, p)}
        onRenameKey={(oldKey, newKey) => renameField('postFields', oldKey, newKey)}
        onAdd={() => addField('postFields')}
        onRemove={(key) => removeField('postFields', key)}
      />

      <div className="sticky bottom-4 flex justify-end">
        <Button
          onClick={() => void handleSave()}
          disabled={!canEdit || saving || userRequiredCount === 0}
          title={userRequiredCount === 0 ? '必須項目が 1 件以上必要です' : undefined}
        >
          {saving ? '保存中…' : '保存'}
        </Button>
      </div>
    </div>
  );
}
